import { useState } from 'react';
import { Search, Heart, ExternalLink, Sparkles } from 'lucide-react';
import { useTricks, useTrickDifficulties, useToggleFavoriteTrick } from '@/queries/hooks';
import { useAuth } from '@/contexts/auth-context';

const DIFFICULTY_COLORS: Record<number, string> = {
  1: 'bg-sky-500',
  2: 'bg-green-500',
  3: 'bg-orange-600',
  4: 'bg-red-600',
  5: 'bg-purple-500',
  6: 'bg-gray-900',
};

const DIFFICULTY_TEXT_COLORS: Record<number, string> = {
  1: 'text-sky-400',
  2: 'text-green-400',
  3: 'text-orange-400',
  4: 'text-red-400',
  5: 'text-purple-400',
  6: 'text-gray-400',
};

export function TricksPage() {
  const [search, setSearch] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const { isAuthenticated } = useAuth();

  const { data: tricks, isLoading } = useTricks();
  const { data: difficulties } = useTrickDifficulties();
  const toggleFavorite = useToggleFavoriteTrick();

  const filteredTricks = tricks?.filter((trick) => {
    if (search) {
      const searchLower = search.toLowerCase();
      const matches =
        trick.name.toLowerCase().includes(searchLower) ||
        trick.description?.toLowerCase().includes(searchLower) ||
        trick.locationName?.toLowerCase().includes(searchLower);
      if (!matches) return false;
    }
    return true;
  }) ?? [];

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-center gap-3">
        <Sparkles className="text-zelda-gold" size={28} />
        <h1 className="text-2xl font-bold text-zelda-gold">OoT Tricks</h1>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
          <input
            type="text"
            placeholder="Search tricks..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:border-zelda-gold focus:outline-none"
          />
        </div>

        {isAuthenticated && (
          <button
            onClick={() => setFavoritesOnly(!favoritesOnly)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors
              ${favoritesOnly
                ? 'bg-red-900/30 border-red-900/50 text-red-400'
                : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'
              }`}
          >
            <Heart size={14} fill={favoritesOnly ? 'currentColor' : 'none'} />
            Favorites
          </button>
        )}
      </div>

      {/* Difficulty legend */}
      {difficulties && (
        <div className="flex flex-wrap gap-2">
          {difficulties.map((d) => (
            <span
              key={d.id}
              className={`px-2 py-0.5 rounded text-xs font-medium text-white ${DIFFICULTY_COLORS[d.id] ?? 'bg-gray-700'}`}
            >
              {d.name}
            </span>
          ))}
        </div>
      )}

      {/* Tricks list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-zelda-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredTricks.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No tricks found matching your criteria.
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTricks.map((trick) => (
            <div
              key={trick.id}
              className="bg-gray-900/50 border border-gray-800 rounded-xl p-4 hover:border-gray-700 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-white truncate">{trick.name}</h3>
                    {trick.locationName && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-800 text-gray-400 flex-shrink-0">
                        {trick.locationName}
                      </span>
                    )}
                  </div>

                  {trick.description && (
                    <p className="text-sm text-gray-400 mb-2">{trick.description}</p>
                  )}

                  {/* Links */}
                  {trick.links && trick.links.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {trick.links.map((link) => (
                        <a
                          key={link.id}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium
                            ${DIFFICULTY_COLORS[link.difficultyId ?? 0] ?? 'bg-gray-700'} text-white
                            hover:opacity-80 transition-opacity`}
                        >
                          {link.name}
                          <ExternalLink size={10} />
                        </a>
                      ))}
                    </div>
                  )}
                </div>

                {/* Favorite button */}
                {isAuthenticated && (
                  <button
                    onClick={() => toggleFavorite.mutate(trick.id)}
                    className="text-gray-600 hover:text-red-400 transition-colors flex-shrink-0"
                    title="Toggle favorite"
                  >
                    <Heart size={18} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
